import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';

import { cn } from '../../../lib/cn';

/**
 * The label-and-message shell every control in `forms/choice` sits in.
 *
 * Label on one row, control on the next (spec §10). `inline` flips that for the
 * two controls that own their label next to themselves — a checkbox and a
 * switch — and `group` swaps the `<label for>` for a `<fieldset>`/`<legend>`,
 * which is the only correct naming for a group of radios.
 *
 * Precedence is error → success → hint, and exactly one of them is rendered.
 * That is deliberately the same order `useFieldIds` resolves, so the
 * `aria-describedby` the control sets can never name a node that is not there.
 */
export interface ChoiceFieldProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  /**
   * The control's id. `ChoiceField` derives the message ids from it, so the
   * control must call `useFieldIds` with the same id.
   */
  id: string;
  label: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Present → the control sets `aria-invalid` and the message is announced. */
  error?: string | null;
  /** Inline-save confirmation, e.g. "Saved!". */
  success?: string | null;
  /** Persistent helper copy, shown only when there is no error or success. */
  hint?: string | null;
  /** Naming for a group of controls — renders a fieldset/legend. */
  group?: boolean;
  /** Label beside the control rather than above it (checkbox, switch). */
  inline?: boolean;
  children: ReactNode;
}

const MESSAGE_STYLES = 'flex items-start gap-1 text-xs leading-snug';

/**
 * The label element, rendered here rather than through `forms/text`'s `Label`.
 * That `Label` is a bare `<label>` and this one is not: `group` needs a
 * `<legend>`, and a group has no control to carry `aria-required`, so the word
 * "required" has to be in the label itself. Its classes differ too
 * (`flex`/`gap-1.5` against this one's `inline-flex`/`gap-1`) — both now track
 * the same `tracking-label`, which is the point of publishing it. Rendering it
 * here is what keeps the markup of every choice control byte-identical.
 */
const LABEL_STYLES = 'inline-flex items-center gap-1 font-display text-2xs font-semibold uppercase tracking-label';

export function ChoiceField({
  id,
  label,
  required = false,
  disabled = false,
  error,
  success,
  hint,
  group = false,
  inline = false,
  className,
  children,
  ...rest
}: ChoiceFieldProps) {
  // Widened to `ElementType` on purpose: a `'fieldset' | 'div'` union makes TypeScript
  // check the spread against BOTH prop types at once, and `ref` — which React 19
  // passes as an ordinary prop — cannot be a div ref and a fieldset ref together.
  const Tag = (group ? 'fieldset' : 'div') as ElementType;
  const LabelTag = (group ? 'legend' : 'label') as ElementType;

  const labelNode = (
    <LabelTag
      // `<label for>` only names a control in the non-group case; a `<fieldset>` named
      // by a `<legend>` names the group, not the input inside it. The id is what lets a
      // grouped control point `aria-labelledby` at this element — see `Slider`'s thumbs.
      id={`${id}-label`}
      htmlFor={group ? undefined : id}
      className={cn(LABEL_STYLES, disabled ? 'text-muted' : 'text-heading', inline && 'cursor-pointer leading-snug')}
    >
      {label}
      {required ? (
        <>
          <span aria-hidden="true" className="text-danger">
            *
          </span>
          <span className="dhd-sr-only">required</span>
        </>
      ) : null}
    </LabelTag>
  );

  // Only one message exists at a time. `role="alert"` on the error is what makes
  // it announced the moment it appears; success is polite, hint is silent.
  const message = error ? (
    <p id={`${id}-error`} role="alert" className={cn(MESSAGE_STYLES, 'text-danger')}>
      <span aria-hidden="true">!</span>
      {error}
    </p>
  ) : success ? (
    <p id={`${id}-success`} role="status" className={cn(MESSAGE_STYLES, 'text-success')}>
      <span aria-hidden="true">✓</span>
      {success}
    </p>
  ) : hint ? (
    <p id={`${id}-hint`} className={cn(MESSAGE_STYLES, 'text-muted')}>
      {hint}
    </p>
  ) : null;

  return (
    <Tag
      {...rest}
      disabled={group ? disabled : undefined}
      className={cn('flex min-w-0 flex-col gap-1.5 border-0 p-0', className)}
    >
      {inline ? (
        <div className="flex items-start gap-2.5">
          {children}
          {labelNode}
        </div>
      ) : (
        <>
          {labelNode}
          {children}
        </>
      )}
      {message}
    </Tag>
  );
}
